import debug from "./debug";
import {isIOS} from "./device";
import {getEventTarget} from "./event";

class ImageLoader {
  /**
   * @param {Object[]} imagesLoad
   * @param {String} imagesLoad[].selector
   * @param {Object} imagesLoad[].thumbnails
   * @param {Object} imagesLoad[].thumbnail.srcSet
   * @param {String} imagesLoad[].thumbnail.srcSet.jpeg
   * @param {String} imagesLoad[].thumbnail.srcSet.jp2
   * @param {String} imagesLoad[].thumbnail.srcSet.png
   * @param {String} imagesLoad[].thumbnail.srcSet.webp
   * @param {String} imagesLoad[].thumbnail.src
   * @param {String} imagesLoad[].thumbnail.sizes
   * @param {Object} imagesLoad[].fullSize
   * @param {String} imagesLoad[].fullSize.srcSet.jpeg
   * @param {String} imagesLoad[].fullSize.srcSet.jp2
   * @param {String} imagesLoad[].fullSize.srcSet.png
   * @param {String} imagesLoad[].fullSize.srcSet.webp
   * @param {String} imagesLoad[].fullSize.src
   * @param {String} imagesLoad[].fullSize.sizes
   * @param {Boolean} imagesLoad[].asBackground
   */
  constructor(imagesLoad) {
    this.imagesLoad = imagesLoad;
  }

  supportFormatWebp() {
    try {
      const img = document.createElement("img");
      img.src = "data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAUAmJaQAA3AA/vz0AAA=";

      return true;
    } catch (e) {
      return false;
    }
  }

  upgradeThumbnail(e) {
    const target = getEventTarget(e);
    const elements = document.querySelectorAll(target.selector);

    debug(["🐛 %cImageLoader ~ upgradeThumbnail", "font-weight:bold"], "Full size image loaded 👉",
      target.src, "👈");

    elements.forEach(elem => {
      if (target.asBackground) {
        elem.style.backgroundImage = `url(${target.src})`;
      } else {
        const thumbnail = elem.querySelector(".auto-loaded--thumbnail");
        const wrapper = thumbnail.parentNode;
        const node = target.cloneNode(true);
        // Remove listener
        node.removeEventListener("load", this.upgradeThumbnail);
        wrapper.replaceChild(node, thumbnail);
      }
      // Remove blur
      elem.classList.add("blur--remove");
    });
    target.removeEventListener("load", this.upgradeThumbnail);
  }

  attachThumbnail(e) {
    const target = getEventTarget(e);
    const elements = document.querySelectorAll(target.selector);
    debug(["🐛 %cImageLoader ~ attachThumbnail", "font-weight:bold"], "Thumbnail loaded 👉",
      target.src, "👈");

    // For elements with matched selector
    elements.forEach(elem => {
      if (target.asBackground) {
        elem.style.backgroundImage = `url(${target.src})`;
      } else {
        const node = target.cloneNode(true);
        // Remove listener
        node.removeEventListener("load", this.attachThumbnail);
        // Copy image into element
        elem.appendChild(node);
      }
    });

    // Remove listener
    target.removeEventListener("load", this.attachThumbnail);

    // Upgrade thumbnail into bigger image
    if (target.fullsize) {
      debug(["🐛 %cImageLoader ~ attachThumbnail", "font-weight:bold"], "Loading full size image 👉",
        target.fullsize, "👈", "with sizes 👉", target.fullsizeSizes, "👈", "with set 👉",
        target.fullsizeSet, "👈");

      const fullSize = document.createElement("img");
      fullSize.classList.add("auto-loaded", "auto-loaded--full-size");
      fullSize.addEventListener("load", function(e) {
        this.upgradeThumbnail(e);
      }.bind(this));
      fullSize.selector = target.selector;
      fullSize.srcset = target.fullsizeSet;
      fullSize.sizes = target.fullsizeSizes;
      fullSize.src = target.fullsize;
      fullSize.asBackground = target.asBackground;
    }
  }

  load() {
    let format, alphaFormat;

    if (isIOS()) {
      format = "jpeg";
      alphaFormat = "jp2";
    } else if (this.supportFormatWebp()) {
      format = alphaFormat = "webp";
    } else {
      format = "jpeg";
      alphaFormat = "png";
    }

    let autoload;
    for (const image of this.imagesLoad) {
      if (!document.querySelector(image.selector)) {
        continue;
      }

      // Blur
      document.querySelector(image.selector).classList.add("blur");

      // Create image tag
      autoload = document.createElement("img");
      autoload.classList.add("auto-loaded", "auto-loaded--thumbnail");
      autoload.format = image.alpha ? alphaFormat : format;
      autoload.selector = image.selector;
      autoload.asBackground = !!image.asBackground;

      // Set full size params
      autoload.fullsizeSet = image.fullSize.srcSet[autoload.format];
      autoload.fullsizeSizes = image.fullSize.sizes;
      autoload.fullsize = image.fullSize.src;

      // Set thumbnail params on image to load
      debug(["🐛 %cImageLoader ~ load", "font-weight:bold"], "Loading thumbnail 👉",
        image.thumbnail.src, "👈", "with sizes 👉", image.thumbnail.sizes, "👈", "with set 👉",
        image.thumbnail.srcSet[autoload.format], "👈");
      autoload.addEventListener("load", function(e) {
        this.attachThumbnail(e);
      }.bind(this));
      autoload.srcset = image.thumbnail.srcSet[autoload.format];
      autoload.sizes = image.thumbnail.sizes;
      autoload.src = image.thumbnail.src;
    }
  }
}

export default ImageLoader;
